import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Eye, MousePointer, TrendingUp, Calendar, MapPin, Megaphone, Search, ExternalLink, Clock, CheckCircle, XCircle, Pause } from 'lucide-react';

function AdList({ user }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadAds = useCallback(async () => {
    try {
      setLoading(true);
      const adsRef = collection(db, 'advertisements');
      let q;
      if (filter === 'all') {
        q = query(adsRef, orderBy('createdAt', 'desc'));
      } else {
        q = query(adsRef, where('status', '==', filter), orderBy('createdAt', 'desc'));
      }
      const snapshot = await getDocs(q);
      const adsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAds(adsList);
    } catch (error) {
      console.error('광고 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadAds(); }, [loadAds]);

  const filteredAds = ads.filter(ad => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return ad.title?.toLowerCase().includes(searchLower) || ad.description?.toLowerCase().includes(searchLower) || ad.advertiserName?.toLowerCase().includes(searchLower);
  });

  const stats = {
    total: ads.length,
    active: ads.filter(ad => ad.status === 'active').length,
    pending: ads.filter(ad => ad.status === 'pending').length,
    totalImpressions: ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0),
    totalClicks: ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0),
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { icon: <CheckCircle size={14} />, label: '활성', bg: '#d1fae5', color: '#065f46' },
      pending: { icon: <Clock size={14} />, label: '대기중', bg: '#fef3c7', color: '#92400e' },
      paused: { icon: <Pause size={14} />, label: '일시정지', bg: '#fee2e2', color: '#991b1b' },
      rejected: { icon: <XCircle size={14} />, label: '거부됨', bg: '#fce7f3', color: '#be123c' }
    };
    const config = configs[status] || configs.pending;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: config.bg, color: config.color, borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>
        {config.icon}{config.label}
      </span>
    );
  };

  const getTierBadge = (tier) => {
    const configs = {
      premium: { label: '프리미엄', bg: '#fef3c7', color: '#92400e' },
      standard: { label: '스탠다드', bg: '#dbeafe', color: '#1e40af' },
      basic: { label: '베이직', bg: '#f3f4f6', color: '#374151' }
    };
    const config = configs[tier] || configs.basic;
    return (
      <span style={{ padding: '4px 10px', backgroundColor: config.bg, color: config.color, fontSize: '11px', fontWeight: '700', borderRadius: '6px' }}>
        {config.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return timestamp.toDate().toLocaleDateString('ko-KR');
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>로딩 중...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}><Megaphone size={32} color="#6366f1" />등록된 광고 목록</h1>
        <p style={styles.subtitle}>플랫폼에 등록된 모든 광고를 확인하세요</p>
      </div>

      {/* 통계 */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#eef2ff', color: '#6366f1' }}><Megaphone size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>전체 광고</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#d1fae5', color: '#10b981' }}><CheckCircle size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.active}</div><div style={styles.statLabel}>활성 광고</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7', color: '#f59e0b' }}><Eye size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.totalImpressions.toLocaleString()}</div><div style={styles.statLabel}>총 노출</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fce7f3', color: '#ec4899' }}><MousePointer size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.totalClicks.toLocaleString()}</div><div style={styles.statLabel}>총 클릭</div></div>
        </div>
      </div>

      {/* 필터 & 검색 */}
      <div style={styles.controls}>
        <div style={styles.filterButtons}>
          {[{ key: 'all', label: '전체' }, { key: 'active', label: '활성' }, { key: 'pending', label: '대기중' }, { key: 'paused', label: '일시정지' }].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={filter === f.key ? { ...styles.filterButton, ...styles.filterButtonActive } : styles.filterButton}>
              {f.label}
            </button>
          ))}
        </div>
        <div style={styles.searchBox}>
          <Search size={20} color="#94a3b8" />
          <input type="text" placeholder="광고 제목, 설명으로 검색..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={styles.searchInput} />
        </div>
      </div>

      {/* 광고 목록 */}
      {filteredAds.length === 0 ? (
        <div style={styles.emptyState}>
          <Megaphone size={64} color="#cbd5e1" />
          <h3 style={styles.emptyTitle}>등록된 광고가 없습니다</h3>
          <p style={styles.emptyText}>아직 등록된 광고가 없습니다.</p>
        </div>
      ) : (
        <div style={styles.adsList}>
          {filteredAds.map(ad => (
            <div key={ad.id} style={styles.adCard}>
              {ad.imageUrl && (
                <div style={styles.imageContainer}>
                  <img src={ad.imageUrl} alt={ad.title} style={styles.adImage} />
                  <div style={styles.tierBadgeOverlay}>{getTierBadge(ad.tier)}</div>
                </div>
              )}
              <div style={styles.adContent}>
                <div style={styles.adHeader}>
                  <div>
                    <h3 style={styles.adTitle}>{ad.title}</h3>
                    <p style={styles.adDescription}>{ad.description}</p>
                  </div>
                  {getStatusBadge(ad.status)}
                </div>
                <div style={styles.advertiserInfo}>
                  <span style={styles.advertiserLabel}>광고주:</span>
                  <span style={styles.advertiserName}>{ad.advertiserName || '알 수 없음'}</span>
                </div>
                <div style={styles.adStats}>
                  <div style={styles.statItem}><Eye size={16} color="#64748b" /><span>노출 {(ad.impressions || 0).toLocaleString()}</span></div>
                  <div style={styles.statItem}><MousePointer size={16} color="#64748b" /><span>클릭 {(ad.clicks || 0).toLocaleString()}</span></div>
                  <div style={styles.statItem}><TrendingUp size={16} color="#64748b" /><span>CTR {ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00'}%</span></div>
                </div>
                <div style={styles.adMeta}>
                  <span style={styles.metaItem}><MapPin size={14} />{ad.position === 'top-banner' ? '상단 배너' : ad.position === 'sidebar' ? '사이드바' : '하단'}</span>
                  <span style={styles.metaItem}><Calendar size={14} />{formatDate(ad.startDate)} ~ {formatDate(ad.endDate)}</span>
                  {ad.url && (
                    <a href={ad.url} target="_blank" rel="noopener noreferrer" style={styles.linkButton}>
                      <ExternalLink size={14} />링크 보기
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '24px' },
  header: { marginBottom: '24px' },
  title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  statIcon: { width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' },
  statContent: { flex: 1 },
  statValue: { fontSize: '24px', fontWeight: '700', color: '#0f172a' },
  statLabel: { fontSize: '13px', color: '#64748b' },

  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' },
  filterButtons: { display: 'flex', gap: '8px' },
  filterButton: { padding: '10px 20px', backgroundColor: '#f8fafc', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
  filterButtonActive: { backgroundColor: '#6366f1', color: 'white', borderColor: '#6366f1' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', minWidth: '300px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },

  adsList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  adCard: { display: 'flex', gap: '20px', padding: '24px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', transition: 'all 0.2s' },
  imageContainer: { position: 'relative', flexShrink: 0, width: '200px' },
  adImage: { width: '100%', height: '150px', objectFit: 'cover', borderRadius: '12px' },
  tierBadgeOverlay: { position: 'absolute', top: '8px', right: '8px' },

  adContent: { flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' },
  adHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  adTitle: { margin: '0 0 6px 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  adDescription: { margin: 0, fontSize: '14px', color: '#64748b', lineHeight: '1.6' },

  advertiserInfo: { display: 'flex', gap: '8px', fontSize: '13px' },
  advertiserLabel: { color: '#94a3b8', fontWeight: '600' },
  advertiserName: { color: '#475569', fontWeight: '500' },

  adStats: { display: 'flex', gap: '20px' },
  statItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' },

  adMeta: { display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' },
  metaItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#94a3b8' },
  linkButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', transition: 'all 0.2s' },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0' },
  emptyTitle: { margin: '20px 0 8px', fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  emptyText: { margin: 0, fontSize: '14px', color: '#64748b' },

  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
  spinner: { width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};

export default AdList;